import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTornaIndietro } from '../common/navigazione';
import { getCollectible } from '../services/api';
import { Container, Card, Row, Col, Spinner, Alert, Button, Badge } from 'react-bootstrap';

const euro = (v) => (v !== null && v !== undefined && v !== '' ? `€ ${Number(v).toFixed(2)}` : 'N/D');

const CollectibleDetail = () => {
  const [collectible, setCollectible] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const location = useLocation();
  const backTo = location.state?.from || '/collectibles';
  const tornaIndietro = useTornaIndietro('/collectibles');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getCollectible(id);
        setCollectible(response.data);
      } catch (err) {
        setError('Errore nel caricamento del collezionabile.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const renderField = (label, value) => (
    <p><strong>{label}:</strong> {value !== null && value !== undefined && value !== '' ? value : 'N/D'}</p>
  );

  if (loading) return <Spinner animation="border" />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!collectible) return <Alert variant="warning">Collezionabile non trovato.</Alert>;

  const c = collectible;
  const immagine = c.immagine_url
    ? (c.immagine_url.startsWith('http') ? c.immagine_url : `${process.env.REACT_APP_STATIC_FILES_BASE_URL}${c.immagine_url}`)
    : null;
  const dimensioni = [c.altezza_cm, c.larghezza_cm, c.lunghezza_cm].some(v => v != null)
    ? `${c.altezza_cm ?? '-'} × ${c.larghezza_cm ?? '-'} × ${c.lunghezza_cm ?? '-'} cm (A × L × P)`
    : null;

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Dettaglio Collezionabile: {c.nome}</h2>
        <div>
          <Button variant="secondary" onClick={tornaIndietro} className="me-2">Torna alla Lista</Button>
          <Button variant="warning" as={Link} to={`/collectibles/edit/${id}`}>Modifica</Button>
        </div>
      </div>

      {immagine && (
        <Card className="mb-4">
          <Card.Body className="text-center">
            <img src={immagine} alt={c.nome} style={{ maxHeight: '300px', border: '1px solid #dee2e6' }} />
          </Card.Body>
        </Card>
      )}

      <Card className="mb-4">
        <Card.Header>SCHEDA PRODOTTO</Card.Header>
        <Card.Body>
          <Row>
            <Col md={8}>{renderField('Nome', c.nome)}</Col>
            <Col md={4}>{renderField('Categoria', c.categoria_prodotto)}</Col>
          </Row>
          <Row>
            <Col>{renderField('Brand', c.brand)}</Col>
            <Col>{renderField('Linea prodotto', c.linea_prodotto)}</Col>
            <Col>{renderField('Licenza', c.licenza)}</Col>
          </Row>
          <Row>
            <Col>{renderField('Personaggio', c.personaggio)}</Col>
            <Col>{renderField('Artista', c.artista)}</Col>
            <Col>{renderField('Materiale', c.materiale)}</Col>
          </Row>
          {renderField('Dimensioni', dimensioni)}
          {renderField('Data uscita', c.data_uscita ? new Date(c.data_uscita).toLocaleDateString('it-IT') : null)}
          <hr />
          <p><strong>Descrizione:</strong></p>
          <p>{c.descrizione || 'N/D'}</p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>CODICI</Card.Header>
        <Card.Body>
          <Row>
            <Col>{renderField('SKU', c.sku)}</Col>
            <Col>{renderField('Barcode', c.barcode)}</Col>
            <Col>{renderField('Fornitore', c.fornitore?.denominazione)}</Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>PREZZI E MAGAZZINO</Card.Header>
        <Card.Body>
          <Row>
            <Col>{renderField('Prezzo base', euro(c.prezzo_vendita_base))}</Col>
            <Col>{renderField('Prezzo vendita', euro(c.prezzo_vendita))}</Col>
            <Col>{renderField('Prezzo IBS', euro(c.prezzo_ibs))}</Col>
            <Col>{renderField('Prezzo acquisto', euro(c.prezzo_acquisto))}</Col>
          </Row>
          <Row>
            <Col>{renderField('IVA', c.aliquota_iva != null ? `${c.aliquota_iva}%` : null)}</Col>
            <Col>{renderField('Quantità', c.quantita)}</Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>VISIBILITÀ</Card.Header>
        <Card.Body>
          <Badge bg={c.pubblica_su_web ? 'success' : 'secondary'} className="me-2">{c.pubblica_su_web ? 'Pubblicato su web' : 'Non pubblicato su web'}</Badge>
          <Badge bg={c.in_vetrina ? 'success' : 'secondary'} className="me-2">{c.in_vetrina ? 'In vetrina' : 'Non in vetrina'}</Badge>
          <Badge bg={c.da_pubblicare_su_ibs ? 'success' : 'secondary'}>{c.da_pubblicare_su_ibs ? 'Presente su IBS' : 'Non presente su IBS'}</Badge>
        </Card.Body>
      </Card>

      {c.attributi_specifici && Object.keys(c.attributi_specifici).length > 0 && (
        <Card className="mb-4">
          <Card.Header>ATTRIBUTI SPECIFICI</Card.Header>
          <Card.Body>
            {Object.entries(c.attributi_specifici).map(([k, v]) => renderField(k, typeof v === 'object' ? JSON.stringify(v) : String(v)))}
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default CollectibleDetail;
